import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AlertCircle,
  Ban,
  Columns2,
  Delete as DeleteIcon,
  FileDown,
  Edit as EditIcon,
  Plus as PlusIcon,
  RefreshCw,
  Search
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const formatDateTimeIt = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const UI_COLORS = {
  indigo: '#4f46e5',
  sky: '#0ea5e9',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444'
};

const translateCategoria = (value) => {
  const map = {
    pratica: 'Pratica',
    contratto: 'Contratto',
    pagamento: 'Pagamento',
    altro: 'Altro'
  };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const translateRuolo = (value) => {
  const map = {
    commerciale: 'Commerciale',
    legale: 'Legale',
    pagamenti: 'Pagamenti',
    entrambi: 'Entrambi'
  };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const translateGravita = (value) => {
  const map = {
    standard: 'Standard',
    grave: 'Grave',
    bloccante: 'Bloccante'
  };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const CategoriaChip = ({ categoria }) => {
  const key = String(categoria || '').toLowerCase();
  const style =
    key === 'pratica'
      ? { color: UI_COLORS.sky }
      : key === 'contratto'
      ? { color: UI_COLORS.indigo }
      : key === 'pagamento'
      ? { color: UI_COLORS.green }
      : { color: UI_COLORS.amber };
  return (
    <Chip
      label={translateCategoria(key) || '-'}
      size="small"
      variant="outlined"
      sx={{
        textTransform: 'capitalize',
        fontWeight: 700,
        color: style.color,
        borderColor: style.color
      }}
    />
  );
};

const RuoloChip = ({ ruolo }) => {
  const key = String(ruolo || '').toLowerCase();
  const style =
    key === 'commerciale'
      ? { color: UI_COLORS.indigo }
      : key === 'legale'
      ? { color: UI_COLORS.sky }
      : key === 'pagamenti'
      ? { color: UI_COLORS.green }
      : { color: UI_COLORS.amber };
  return (
    <Chip
      label={translateRuolo(key) || '-'}
      size="small"
      variant="outlined"
      sx={{
        textTransform: 'capitalize',
        fontWeight: 700,
        color: style.color,
        borderColor: style.color
      }}
    />
  );
};

const GravitaChip = ({ gravita }) => {
  const key = String(gravita || '').toLowerCase();
  const style =
    key === 'bloccante'
      ? { color: UI_COLORS.red }
      : key === 'grave'
      ? { color: UI_COLORS.amber }
      : key === 'standard'
      ? { color: UI_COLORS.indigo }
      : { color: 'inherit' };
  return (
    <Chip
      label={translateGravita(key) || '-'}
      size="small"
      variant="outlined"
      sx={{
        textTransform: 'capitalize',
        fontWeight: 700,
        color: style.color,
        borderColor: style.color
      }}
    />
  );
};

const TotaleSegnalazioniChip = ({ value }) => {
  const n = Number(value || 0);
  return (
    <Chip
      label={Number.isFinite(n) ? String(n) : '0'}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 900,
        color: UI_COLORS.amber,
        borderColor: `${UI_COLORS.amber}80`,
        minWidth: 56,
        justifyContent: 'center'
      }}
    />
  );
};

const PercentDonut = ({ value }) => {
  const raw = Number(value || 0);
  const pct = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 0;
  const size = 38;
  const thickness = 5;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{ color: 'rgba(15, 23, 42, 0.10)' }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={size}
        thickness={thickness}
        sx={{ position: 'absolute', left: 0, color: UI_COLORS.amber }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          {pct}%
        </Typography>
      </Box>
    </Box>
  );
};

const parseBlobError = async (data) => {
  try {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed?.message || text;
      } catch (_) {
        return text;
      }
    }
    if (typeof data === 'object') {
      return data?.message || null;
    }
    return null;
  } catch (_) {
    return null;
  }
};

export default function BlacklistTipiInadempienza() {
  const columnDefs = useMemo(
    () => [
      { key: 'codice', label: 'Codice', sortable: true, align: 'left' },
      { key: 'descrizione', label: 'Descrizione', sortable: true, align: 'left' },
      { key: 'categoria', label: 'Categoria', sortable: true, align: 'left' },
      { key: 'ruolo_riferimento', label: 'Ruolo riferimento', sortable: true, align: 'left' },
      { key: 'gravita', label: 'Gravità', sortable: true, align: 'left' },
      { key: 'totale_segnalazioni', label: 'Totale segnalazioni', sortable: true, align: 'center' },
      { key: 'perc_totale', label: '% sul totale', sortable: true, align: 'center' },
      { key: 'attivo', label: 'Attivo', sortable: true, align: 'center' },
      { key: 'created_at', label: 'Creato il', sortable: true, align: 'left' }
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('attivo');
  const [filterText, setFilterText] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterRuolo, setFilterRuolo] = useState('');
  const [filterGravita, setFilterGravita] = useState('');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'descrizione',
    'categoria',
    'ruolo_riferimento',
    'gravita',
    'totale_segnalazioni',
    'perc_totale',
    'attivo'
  ]);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [formData, setFormData] = useState({
    codice: '',
    descrizione: '',
    categoria: 'altro',
    ruolo_riferimento: 'entrambi',
    gravita: 'standard',
    attivo: true
  });

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/blacklist/tipi-inadempienza');
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching blacklist tipi inadempienza:', err);
      toast.error('Errore nel caricamento dei tipi inadempienza');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (reporting) return;
    setReporting(true);
    try {
      const res = await api.get('/blacklist/tipi-inadempienza/report/pdf', {
        responseType: 'blob',
        params: {
          search: filterText || '',
          categoria: filterCategoria || '',
          ruolo: filterRuolo || '',
          gravita: filterGravita || '',
          onlyActive: onlyActive ? 1 : 0
        }
      });

      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/pdf'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const now = new Date();
      const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
        now.getDate()
      ).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(
        2,
        '0'
      )}`;
      link.href = url;
      link.download = `blacklist_tipi_inadempienza_per_ruolo_${ts}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message = await parseBlobError(error?.response?.data);
      toast.error(message || 'Errore durante la generazione del report');
    } finally {
      setReporting(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrentRow(row);
      setFormData({
        codice: row.codice || '',
        descrizione: row.descrizione || '',
        categoria: row.categoria || 'altro',
        ruolo_riferimento: row.ruolo_riferimento || 'entrambi',
        gravita: row.gravita || 'standard',
        attivo: Boolean(row.attivo)
      });
    } else {
      setCurrentRow(null);
      setFormData({
        codice: '',
        descrizione: '',
        categoria: 'altro',
        ruolo_riferimento: 'entrambi',
        gravita: 'standard',
        attivo: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRow(null);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        codice: sanitizeCodice(formData.codice),
        descrizione: String(formData.descrizione || '').trim(),
        categoria: String(formData.categoria || 'altro').trim().toLowerCase(),
        ruolo_riferimento: String(formData.ruolo_riferimento || 'entrambi').trim().toLowerCase(),
        gravita: String(formData.gravita || 'standard').trim().toLowerCase(),
        attivo: Boolean(formData.attivo)
      };

      if (!payload.codice || !payload.descrizione) {
        toast.error('Codice e descrizione sono obbligatori');
        return;
      }

      if (currentRow?.id) {
        await api.put(`/blacklist/tipi-inadempienza/${currentRow.id}`, payload);
        toast.success('Tipo inadempienza aggiornato');
      } else {
        await api.post('/blacklist/tipi-inadempienza', payload);
        toast.success('Tipo inadempienza creato');
      }

      handleCloseDialog();
      fetchRows();
    } catch (err) {
      console.error('Error saving blacklist tipo inadempienza:', err);
      toast.error(err?.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/blacklist/tipi-inadempienza/${deleteTarget.id}`);
      toast.success('Tipo inadempienza eliminato');
      closeDeleteDialog();
      fetchRows();
    } catch (err) {
      console.error('Error deleting blacklist tipo inadempienza:', err);
      toast.error(err?.response?.data?.message || "Errore durante l'eliminazione");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayedColumns = columnDefs.filter((col) => visibleColumns.includes(col.key));

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.includes(columnKey);
      if (isVisible && prev.length === 1) return prev;
      if (isVisible) return prev.filter((key) => key !== columnKey);
      return [...prev, columnKey];
    });
  };

  const sanitizeCodice = (value) =>
    String(value || '')
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '')
      .slice(0, 50);

  const compareText = (a, b) => {
    const sa = String(a || '').toLowerCase();
    const sb = String(b || '').toLowerCase();
    if (sa < sb) return -1;
    if (sa > sb) return 1;
    return 0;
  };

  const filteredBaseRows = rows.filter((row) => {
    if (onlyActive && !row?.attivo) return false;
    if (filterCategoria && String(row?.categoria || '') !== filterCategoria) return false;
    if (filterRuolo && String(row?.ruolo_riferimento || '') !== filterRuolo) return false;
    if (filterGravita && String(row?.gravita || '') !== filterGravita) return false;
    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return (
      (row.codice || '').toLowerCase().includes(search) ||
      (row.descrizione || '').toLowerCase().includes(search) ||
      (row.categoria || '').toLowerCase().includes(search) ||
      (row.ruolo_riferimento || '').toLowerCase().includes(search) ||
      (row.gravita || '').toLowerCase().includes(search)
    );
  });

  const totaleSegnalazioniFiltered = filteredBaseRows.reduce((acc, row) => {
    const n = Number(row?.totale_segnalazioni || 0);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);

  const filteredRows = filteredBaseRows
    .map((row) => {
      const n = Number(row?.totale_segnalazioni || 0);
      const safeN = Number.isFinite(n) ? n : 0;
      const pct =
        totaleSegnalazioniFiltered > 0
          ? Math.round((safeN / totaleSegnalazioniFiltered) * 100)
          : 0;
      return { ...row, perc_totale: pct };
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      const valA = a[orderBy];
      const valB = b[orderBy];
      if (
        orderBy === 'attivo' ||
        orderBy === 'id' ||
        orderBy === 'totale_segnalazioni' ||
        orderBy === 'perc_totale'
      ) {
        const numA = Number(valA || 0);
        const numB = Number(valB || 0);
        const dir = isAsc ? 1 : -1;
        const primary = (numA - numB) * dir;
        if (primary !== 0 || orderBy === 'id') return primary;
        const secondaryKeys = ['categoria', 'gravita', 'codice'];
        for (const k of secondaryKeys) {
          const cmp = compareText(a[k], b[k]);
          if (cmp !== 0) return cmp;
        }
        return 0;
      }
      if (orderBy === 'created_at') {
        const dateA = valA ? new Date(valA).getTime() : 0;
        const dateB = valB ? new Date(valB).getTime() : 0;
        return isAsc ? dateA - dateB : dateB - dateA;
      }
      const cmp = compareText(valA, valB);
      return isAsc ? cmp : -cmp;
    });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: `${UI_COLORS.red}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Ban size={20} color={UI_COLORS.red} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Tipi inadempienza
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Anagrafica dei criteri di blacklist
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outline" onClick={handleDownloadReport} disabled={reporting} title="Report PDF">
            <div className="flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              <span>Report</span>
            </div>
          </Button>
          <Button variant="outline" onClick={fetchRows} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo tipo
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <CardContent>
          <Grid
            container
            spacing={2}
            sx={{
              mb: 3,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 }
            }}
          >
            <Grid item sx={{ minWidth: 360, flex: '0 0 auto' }}>
              <TextField
                placeholder="Cerca per codice, descrizione, categoria..."
                variant="outlined"
                size="small"
                fullWidth
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item sx={{ minWidth: 180, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={filterCategoria}
                  label="Categoria"
                  onChange={(e) => {
                    setFilterCategoria(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="pratica">Pratica</MenuItem>
                  <MenuItem value="contratto">Contratto</MenuItem>
                  <MenuItem value="pagamento">Pagamento</MenuItem>
                  <MenuItem value="altro">Altro</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sx={{ minWidth: 180, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Ruolo</InputLabel>
                <Select
                  value={filterRuolo}
                  label="Ruolo"
                  onChange={(e) => {
                    setFilterRuolo(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="commerciale">Commerciale</MenuItem>
                  <MenuItem value="legale">Legale</MenuItem>
                  <MenuItem value="pagamenti">Pagamenti</MenuItem>
                  <MenuItem value="entrambi">Entrambi</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sx={{ minWidth: 180, flex: '0 0 auto' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Gravità</InputLabel>
                <Select
                  value={filterGravita}
                  label="Gravità"
                  onChange={(e) => {
                    setFilterGravita(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="grave">Grave</MenuItem>
                  <MenuItem value="bloccante">Bloccante</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item sx={{ minWidth: 260, flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                <Checkbox
                  checked={onlyActive}
                  onChange={(e) => {
                    setOnlyActive(e.target.checked);
                    setPage(0);
                  }}
                  size="small"
                />
                <Typography variant="body2">Solo attivi</Typography>
              </Box>

              <Button
                variant="outline"
                onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                title="Colonne"
              >
                <Columns2 className="h-4 w-4" />
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={Boolean(columnsMenuAnchor)}
                onClose={() => setColumnsMenuAnchor(null)}
              >
                {columnDefs.map((col) => (
                  <MenuItem key={col.key} onClick={() => handleToggleColumn(col.key)}>
                    <Checkbox checked={visibleColumns.includes(col.key)} />
                    {col.label}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {displayedColumns.map((col) => (
                    <TableCell key={col.key} align={col.align}>
                      {col.sortable ? (
                        <TableSortLabel
                          active={orderBy === col.key}
                          direction={orderBy === col.key ? order : 'asc'}
                          onClick={() => handleRequestSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 110 }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 3 }}>
                      Nessun record trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow key={row.id} hover>
                        {displayedColumns.map((col) => {
                          if (col.key === 'categoria') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <CategoriaChip categoria={row.categoria} />
                              </TableCell>
                            );
                          }
                          if (col.key === 'ruolo_riferimento') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <RuoloChip ruolo={row.ruolo_riferimento} />
                              </TableCell>
                            );
                          }
                          if (col.key === 'gravita') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <GravitaChip gravita={row.gravita} />
                              </TableCell>
                            );
                          }
                          if (col.key === 'totale_segnalazioni') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <TotaleSegnalazioniChip value={row.totale_segnalazioni} />
                              </TableCell>
                            );
                          }
                          if (col.key === 'perc_totale') {
                            return (
                              <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
                                <PercentDonut value={row.perc_totale} />
                              </TableCell>
                            );
                          }
                          if (col.key === 'attivo') {
                            return (
                              <TableCell key={col.key} align={col.align}>
                                <Checkbox checked={Boolean(row.attivo)} disabled size="small" />
                              </TableCell>
                            );
                          }
                          if (col.key === 'created_at') {
                            return (
                              <TableCell key={col.key} align={col.align}>
                                {formatDateTimeIt(row.created_at) || 'n/d'}
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={col.key} align={col.align}>
                              {row[col.key] ?? ''}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenDialog(row)}>
                            <EditIcon className="h-4 w-4 text-slate-900 dark:text-slate-100" />
                          </IconButton>
                          <IconButton size="small" onClick={() => openDeleteDialog(row)}>
                            <DeleteIcon className="h-4 w-4 text-red-500" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Righe per pagina"
          />
        </CardContent>
      </StyledCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {currentRow?.id ? 'Modifica tipo inadempienza' : 'Nuovo tipo inadempienza'}
        </DialogTitle>
        <DialogContent>
          <div className="mt-3 grid grid-cols-1 gap-3">
            <TextField
              label="Codice"
              size="small"
              value={formData.codice}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  codice: sanitizeCodice(e.target.value)
                }))
              }
              placeholder="LEG_RITARDO_GRAVE"
            />
            <TextField
              label="Descrizione"
              size="small"
              value={formData.descrizione}
              onChange={(e) => setFormData((p) => ({ ...p, descrizione: e.target.value }))}
              placeholder="Testo descrittivo del criterio"
            />
            <TextField
              select
              label="Categoria"
              size="small"
              value={formData.categoria}
              onChange={(e) => setFormData((p) => ({ ...p, categoria: e.target.value }))}
            >
              <MenuItem value="pratica">Pratica</MenuItem>
              <MenuItem value="contratto">Contratto</MenuItem>
              <MenuItem value="pagamento">Pagamento</MenuItem>
              <MenuItem value="altro">Altro</MenuItem>
            </TextField>
            <TextField
              select
              label="Ruolo riferimento"
              size="small"
              value={formData.ruolo_riferimento}
              onChange={(e) => setFormData((p) => ({ ...p, ruolo_riferimento: e.target.value }))}
            >
              <MenuItem value="commerciale">Commerciale</MenuItem>
              <MenuItem value="legale">Legale</MenuItem>
              <MenuItem value="pagamenti">Pagamenti</MenuItem>
              <MenuItem value="entrambi">Entrambi</MenuItem>
            </TextField>
            <TextField
              select
              label="Gravità"
              size="small"
              value={formData.gravita}
              onChange={(e) => setFormData((p) => ({ ...p, gravita: e.target.value }))}
            >
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="grave">Grave</MenuItem>
              <MenuItem value="bloccante">Bloccante</MenuItem>
            </TextField>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={Boolean(formData.attivo)}
                onChange={(e) => setFormData((p) => ({ ...p, attivo: e.target.checked }))}
              />
              <Typography variant="body2">Attivo</Typography>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Elimina tipo inadempienza</DialogTitle>
        <DialogContent>
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <Typography className="font-semibold">
                  {deleteTarget?.codice || 'n/d'}
                </Typography>
                <Typography variant="body2" color="textSecondary" className="truncate">
                  {deleteTarget?.descrizione || ''}
                </Typography>
              </div>
            </div>
            <Typography variant="body2" color="textSecondary">
              Questa operazione non è reversibile.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
